import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ClockIcon,
  ExclamationCircleIcon, Square3Stack3DIcon,
} from "@heroicons/react/24/solid";
import Checkbox from "../forms/Checkbox";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import HorizontalRadio from "../forms/HorizontalRadio";
import SelectOrUnselectAll from "../forms/SelectOrUnselectAll";
import UploadFileDropzone from "../forms/UploadImageDropzone";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";
import { PromptState, createPrompt, deletePrompts, getPrompts, updatePrompt } from "../../slices/third_party";
import TextArea from "../forms/TextArea";
import { checkIfStringIsValidJson } from "../../utils/placeholders";

export default function Prompts() {
  const dispatch = useAppDispatch();

  // Get query params
  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");

  // Fetch data from server
  useEffect(() => {
    dispatch(getPrompts({ category: searchCategory }));
    dispatch(getRooms());
  }, [dispatch, searchCategory]);

  // Set state
  const [category, setCategory] = useState({ value: "" });
  const [selectedPrompt, setSelectedPrompt] = useState<{
    id: string;
    name: { [key: string]: string };
    category: string;
    text: { [key: string]: string };
    image: string | null;
    showcase: boolean;
    callback_url: string;
    callback_method: string;
    callback_body: string;
    target: string;
    rooms: string[];
    status: string;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    category: "SHO",
    text: { EN: "", FR: "", IT: "", DE: "" },
    image: "",
    showcase: true,
    callback_url: "",
    callback_method: "",
    callback_body: "",
    target: "A",
    rooms: [],
    status: "A",
  });
  const [createPromptForm, setCreatePromptForm] = useState<{
    name: { [key: string]: string };
    category: string;
    text: { [key: string]: string };
    showcase: boolean;
    callback_target: string;
    callback_url: string;
    callback_method: string;
    callback_body: string;
    target: string;
    rooms: string[];
    status: string;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    category: "SHO",
    text: { EN: "", FR: "", IT: "", DE: "" },
    showcase: true,
    callback_target: "I",
    callback_url: "",
    callback_method: "",
    callback_body: "",
    target: "A",
    rooms: [],
    status: "A",
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { prompts, options } = useAppSelector((state) => state.thirdParty);
  const { rooms } = useAppSelector((state) => state.rooms);
  const { images } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectPromptToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [promptId] = args;
    const promptData = promptsData.filter((prompt) => prompt.id === promptId)[0];
    setSelectedPrompt({
      id: promptData.id,
      name: promptData.name,
      category: promptData.category[0],
      text: promptData.text,
      image: promptData.image,
      showcase: promptData.showcase,
      callback_url: promptData.callback_url === null ? "" : promptData.callback_url,
      callback_method: promptData.callback_method === null ? "" : promptData.callback_method[0],
      callback_body: promptData.callback_body === null ? "" : JSON.stringify(promptData.callback_body),
      target: promptData.target[0],
      rooms: promptData.rooms,
      status: promptData.status[0],
    });
    setShowEditModal(true);
  }

  function openCreatePromptModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectOrUnselectAllCreateRooms(e: any, args: any[]) {
    e.preventDefault();
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setCreatePromptForm({ ...createPromptForm, rooms: allAvailableRooms });
    } else {
      setCreatePromptForm({ ...createPromptForm, rooms: [] });
    }
  }

  function updateRoomForCreatePrompt(roomId: string) {
    if (createPromptForm.rooms.includes(roomId)) {
      const updatedRooms = createPromptForm.rooms.filter((x) => x !== roomId);
      setCreatePromptForm({ ...createPromptForm, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createPromptForm.rooms, roomId];
      setCreatePromptForm({ ...createPromptForm, rooms: updatedRooms });
    }
  }

  function selectOrUnselectAllEditRooms(e: any, args: any[]) {
    e.preventDefault();
    if (selectedPrompt === null) return;
    const [selectAll] = args;
    if (selectAll) {
      const allAvailableRooms =
        rooms.data === null ? [] : rooms.data.map((x) => x.id);
      setSelectedPrompt({ ...selectedPrompt, rooms: allAvailableRooms });
    } else {
      setSelectedPrompt({ ...selectedPrompt, rooms: [] });
    }
  }

  function updateRoomForEditPrompt(roomId: string) {
    if (selectedPrompt === null) return;
    if (selectedPrompt.rooms.includes(roomId)) {
      const updatedRooms = selectedPrompt.rooms.filter((x) => x !== roomId);
      setSelectedPrompt({ ...selectedPrompt, rooms: updatedRooms });
    } else {
      const updatedRooms = [...createPromptForm.rooms, roomId];
      setSelectedPrompt({ ...selectedPrompt, rooms: updatedRooms });
    }
  }

  async function confirmCreatePrompt(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      category,
      text,
      showcase,
      callback_url: callbackUrl,
      callback_method: callbackMethod,
      callback_body: callbackBody,
      target,
      rooms,
      status
    } = createPromptForm;

    // Validate name
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate text
    if (text[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_text", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = rooms;
    if (target === "A") chosenRooms = roomsData.map((x: any) => x.id);
    if (chosenRooms.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_rooms", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Add item image if uploaded
    const processedPhotoKeys = Object.keys(images.prompts.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.prompts.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }

    const parsedBody = callbackBody === "" ? null : JSON.parse(callbackBody);

    setShowCreateModal(false);
    const res: any = await dispatch(
      createPrompt({
        name,
        property: user.property.id,
        category,
        text,
        image: processedPhotos.length > 0 ? processedPhotos[0] : null,
        showcase,
        callbackUrl: callbackUrl === "" ? null : callbackUrl,
        callbackMethod: callbackMethod === "" ? null : callbackMethod,
        callbackBody: parsedBody,
        target,
        rooms: chosenRooms,
        status
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_prompt", language), "success", 3000),
      );
      setCreatePromptForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        category: "SHO",
        text: { EN: "", FR: "", IT: "", DE: "" },
        showcase: true,
        callback_target: "I",
        callback_url: "",
        callback_method: "",
        callback_body: "",
        target: "A",
        rooms: [],
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditPrompt(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      category,
      text,
      image,
      showcase,
      callback_url: callbackUrl,
      callback_method: callbackMethod,
      callback_body: callbackBody,
      target,
      rooms,
      status
    } = selectedPrompt;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (text[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_text", language), "danger", 5000),
      );
      return;
    }

    // Validate rooms
    let chosenRooms: string[] = rooms;
    if (target === "A") chosenRooms = roomsData.map((x: any) => x.id);
    if (chosenRooms.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_rooms", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Add item image if uploaded
    const savedImage: any = image;
    const processedPhotoKeys = Object.keys(images.prompts.data);
    const processedPhotos = [];
    for (let i = 0; i < processedPhotoKeys.length; i += 1) {
      const key = processedPhotoKeys[i];
      const candidate = images.prompts.data[key];
      if (candidate.data === null || !candidate.success) {
        continue;
      }
      processedPhotos.push({
        id: key,
        source: "processed",
        data: candidate.data,
      });
    }
    let editImage: any = null;
    if (!images.prompts.removed.includes(savedImage) && savedImage !== null) {
      editImage = { id: 0, source: "saved", data: savedImage };
    } else {
      editImage = processedPhotos.length > 0 ? processedPhotos[0] : null;
    }

    const parsedBody = callbackBody === "" ? null : JSON.parse(callbackBody);

    setShowEditModal(false);
    const res: any = await dispatch(
      updatePrompt({
        promptId: id,
        name,
        property: user.property.id,
        category,
        text,
        image: editImage,
        showcase,
        callbackUrl: callbackUrl === "" ? null : callbackUrl,
        callbackMethod: callbackMethod === "" ? null : callbackMethod,
        callbackBody: parsedBody,
        target,
        rooms: chosenRooms,
        status
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_prompt", language), "success", 3000),
      );
      setSelectedPrompt({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        category: "SHO",
        text: { EN: "", FR: "", IT: "", DE: "" },
        image: "",
        showcase: true,
        callback_url: "",
        callback_method: "",
        callback_body: "",
        target: "A",
        rooms: [],
        status: "A",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeletePrompt(e: any, args: any[]) {
    e.preventDefault();
    const [promptIds] = args;
    if (promptIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (promptIds === null || promptIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_prompts", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(deletePrompts({ promptIds: [promptIds] }));
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function getSavedImages() {
    if (selectedPrompt === null) return [];
    if (selectedPrompt.image === null) return [];
    if (images.prompts.removed.includes(selectedPrompt.image))
      return [];
    return [
      {
        id: selectedPrompt.image,
        data: selectedPrompt.image,
      },
    ];
  }

  function checkBodyIsValid(bodyStr: string | null) {
    if (bodyStr === null) return true;
    return checkIfStringIsValidJson(bodyStr);
  }

  // Format rooms data and filter by query param tag
  const roomsData = rooms.data === null ? [] : rooms.data;
  const promptsData = prompts.data === null ? [] : prompts.data;
  const promptCategories =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
        value: key,
        label: options.category[key][language],
      }));
  const promptCategoryChoices =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => [
        key,
        options.category[key][language],
      ]);
  const targetTypes = [
    ["A", translateText("all", language)],
    ["S", translateText("specific", language)],
  ];
  const callbackTargetTypes = [
    ["I", translateText("audico", language)],
    ["E", translateText("other_destination", language)],
  ]
  const callbackMethodChoices = [
    ["", ""],
    ["G", "GET"],
    ["PO", "POST"],
    ["PU", "PUT"],
    ["PA", "DELETE"],
  ]


  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("prompts_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreatePromptModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_prompt", language)}
              </Button>
            }
          />
        }
      />

      {/* Create prompt modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_prompt", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreatePrompt}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_prompt", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createPromptForm}
                stateName="name"
                onChange={setCreatePromptForm}
                defaultLanguage={language}
                languageChoices={
                  promptsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(promptsData[0].name)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("category", language)}
                showLabel={true}
                required={true}
                state={createPromptForm}
                stateName="category"
                onChange={setCreatePromptForm}
                defaultValue="SHO"
              >
                {promptCategoryChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Text */}
              <TextInputMultiLanguage
                id="text"
                type="text"
                label={translateText("text", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={false}
                showLabel={true}
                state={createPromptForm}
                stateName="text"
                onChange={setCreatePromptForm}
                defaultLanguage={language}
                languageChoices={
                  promptsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(promptsData[0].text)
                }
              />

              {/* Image */}
              <div>
                <label
                  htmlFor="payment_item_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("image", language)}
                </label>
                <UploadFileDropzone
                  id="prompts_image"
                  className=""
                  limit={1}
                  page="prompts"
                  savedImages={[]}
                  blockId={null}
                />
              </div>

              {/* Showcase */}
              <SwitchToggle
                id="showcase"
                label={translateText("showcase", language)}
                description={translateText("showcase_prompt_subtext", language)}
                showLabel={true}
                toggle={selectedPrompt.showcase === true}
                state={selectedPrompt}
                stateName="showcase"
                handler={setSelectedPrompt}
                mapping={{ true: true, false: false }}
              />

              {/* Callback target */}
              <HorizontalRadio
                label={translateText("callback_target", language)}
                small={translateText("callback_target_subtext", language)}
              >
                <>
                  {callbackTargetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="callback_target"
                        type="radio"
                        defaultChecked={createPromptForm.callback_target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreatePromptForm({
                            ...createPromptForm,
                            callback_target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {createPromptForm.callback_target === "E" && (
                <>
                  {/* Callback URL */}
                  <TextInput
                    id="callback_url"
                    type="text"
                    label={translateText("callback_url", language)}
                    className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                    required={false}
                    showLabel={true}
                    state={createPromptForm}
                    stateName="callback_url"
                    onChange={setCreatePromptForm}
                  />

                  {/* Callback method */}
                  <Dropdown
                    id="callback_method"
                    className="col-span-full"
                    label={translateText("callback_method", language)}
                    showLabel={true}
                    required={true}
                    state={createPromptForm}
                    stateName="callback_method"
                    onChange={setCreatePromptForm}
                    defaultValue=""
                  >
                    {callbackMethodChoices.map((opt) => (
                      <option key={opt[0]} value={opt[0]}>
                        {opt[1]}
                      </option>
                    ))}
                  </Dropdown>

                  {/* Callback body */}
                  {createPromptForm.callback_method !== "G" && (
                    <TextArea
                      id="body"
                      label={translateText("body", language)}
                      className={
                        checkBodyIsValid(createPromptForm.callback_body) === true
                          ? "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm"
                          : "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm"
                      }
                      required={true}
                      showLabel={true}
                      state={createPromptForm}
                      stateName="callback_body"
                      onChange={setCreatePromptForm}
                      placeholder={translateText(
                        "callback_body_placeholder_prompts",
                        language,
                      )}
                      small={translateText("callback_body_subtext_prompts", language)}
                      rows={8}
                    />
                  )}
                </>
              )}

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={createPromptForm.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreatePromptForm({
                            ...createPromptForm,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* Rooms */}
              {createPromptForm.target === "S" && (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllCreateRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForCreatePrompt}
                        checked={createPromptForm.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </Modal>

      {/* Edit prompt modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_prompt", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditPrompt}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_prompt", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedPrompt}
                stateName="name"
                onChange={setSelectedPrompt}
                defaultLanguage={language}
                languageChoices={
                  promptsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(promptsData[0].name)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("category", language)}
                showLabel={true}
                required={true}
                state={selectedPrompt}
                stateName="category"
                onChange={setSelectedPrompt}
                defaultValue={selectedPrompt.category}
              >
                {promptCategoryChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Text */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("text", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedPrompt}
                stateName="text"
                onChange={setSelectedPrompt}
                defaultLanguage={language}
                languageChoices={
                  promptsData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(promptsData[0].name)
                }
              />

              {/* Image */}
              <div>
                <label
                  htmlFor="prompt_edit_image"
                  className={`mb-3 block text-left text-sm font-medium text-black`}
                >
                  {translateText("image", language)}
                </label>
                <UploadFileDropzone
                  id="prompt_edit_image"
                  className=""
                  limit={1}
                  page="prompts"
                  savedImages={getSavedImages()}
                  blockId={null}
                />
              </div>

              {/* Showcase */}
              <SwitchToggle
                id="showcase"
                label={translateText("showcase", language)}
                description={translateText("showcase_prompt_subtext", language)}
                showLabel={true}
                toggle={selectedPrompt.showcase === true}
                state={selectedPrompt}
                stateName="showcase"
                handler={setSelectedPrompt}
                mapping={{ true: true, false: false }}
              />

              {/* Callback URL */}
              <TextInput
                id="callback_url"
                type="text"
                label={translateText("callback_url", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={false}
                showLabel={true}
                state={selectedPrompt}
                stateName="callback_url"
                onChange={setSelectedPrompt}
              />

              {/* Callback method */}
              <Dropdown
                id="callback_method"
                className="col-span-full"
                label={translateText("callback_method", language)}
                showLabel={true}
                required={true}
                state={selectedPrompt}
                stateName="callback_method"
                onChange={setSelectedPrompt}
                defaultValue={selectedPrompt.callback_method}
              >
                {callbackMethodChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Callback body */}
              {selectedPrompt.callback_method !== "G" && (
                <TextArea
                  id="body"
                  label={translateText("body", language)}
                  className={
                    checkBodyIsValid(selectedPrompt.callback_body) === true
                      ? "block w-full appearance-none rounded-md border border-success bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-success focus:bg-light/40 focus:outline-none focus:ring-success sm:text-sm"
                      : "block w-full appearance-none rounded-md border border-danger bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-danger focus:bg-light/40 focus:outline-none focus:ring-danger sm:text-sm"
                  }
                  required={true}
                  showLabel={true}
                  state={selectedPrompt}
                  stateName="callback_body"
                  onChange={setSelectedPrompt}
                  placeholder={translateText(
                    "callback_body_placeholder_prompts",
                    language,
                  )}
                  small={translateText("callback_body_subtext_prompts", language)}
                  rows={8}
                />
              )}

              {/* Target */}
              <HorizontalRadio
                label={translateText("target_rooms", language)}
                small={translateText("target_rooms_subtext", language)}
              >
                <>
                  {targetTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={selectedPrompt.target === choice[0]}
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setSelectedPrompt({
                            ...selectedPrompt,
                            target: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* Rooms */}
              {selectedPrompt.target === "S" && (
                <>
                  <div>
                    <label className="mb-1 block text-left text-sm font-medium text-black">
                      {translateText("rooms", language)}
                    </label>
                    <p className="text-left text-xs italic text-secondary">
                      {translateText("rooms_template_subtext", language)}
                    </p>
                  </div>

                  {/* Select/unselect all buttons */}
                  <SelectOrUnselectAll
                    handler={selectOrUnselectAllEditRooms}
                    language={language}
                  />
                  <div className="grid md:grid-cols-3 grid-cols-1 gap-2">
                    {roomsData.map((room: any) => (
                      <Checkbox
                        key={room.id}
                        id={room.id}
                        onChange={updateRoomForEditPrompt}
                        checked={selectedPrompt.rooms.includes(room.id)}
                        label={room.name}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText("status_event_subtext", language)}
                showLabel={true}
                toggle={selectedPrompt.status === "A"}
                state={selectedPrompt}
                stateName="status"
                handler={setSelectedPrompt}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedPrompt.id}
                handler={confirmDeletePrompt}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {prompts.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: promptCategories,
                },
              ]}
              queryParams={[
                ["category", searchCategory],
              ]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
              }}
              loading={prompts.loading}
              baseUrl="/third-party/prompts"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {promptsData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {promptsData.map((prompt: PromptState) => (
                    <a
                      href="#!"
                      key={prompt.id}
                      onClick={(e) => selectPromptToEdit(e, [prompt.id])}
                      className="relative"
                    >
                      {/* Card data */}
                      <Card
                        key={prompt.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {prompt.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <Square3Stack3DIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {prompt.category[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {prompt.datetime_created}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={prompt.status[0] === "A" ? "success" : "warning"}
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_prompts", language)}
                subtext={translateText("no_prompts_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
